/**
 * Third-Party Scripts Lazy Loader
 * Optimizes performance by loading external scripts after main content
 */

interface ThirdPartyConfig {
  googleAnalytics: {
    id: string;
    enabled: boolean;
  };
  googleAdsense: {
    client: string;
    enabled: boolean;
  };
}

interface LoadingState {
  analytics: boolean;
  adsense: boolean;
  initialized: boolean;
}

class ThirdPartyLoader {
  private config: ThirdPartyConfig;
  private loadingState: LoadingState;
  private observers: IntersectionObserver[] = [];
  private timeouts: NodeJS.Timeout[] = [];

  constructor() {
    this.config = {
      googleAnalytics: {
        id: 'G-6QLQYFHSYV',
        enabled: false
      },
      googleAdsense: {
        client: 'ca-pub-8980858398695883',
        enabled: process.env.NODE_ENV === 'production'
      }
    };

    this.loadingState = {
      analytics: false,
      adsense: false,
      initialized: false
    };

    this.init();
  }

  private init(): void {
    if (this.loadingState.initialized) return;
    this.loadingState.initialized = true;

    console.log('🚀 [ThirdPartyLoader] Initializing lazy loading system');
    this.setupLoadingTriggers();
  }

  private getOptimalDelay(): number {
    let delay = 1500; // Base delay

    // Adjust for connection speed
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection?.effectiveType) {
        switch (connection.effectiveType) {
          case 'slow-2g':
          case '2g':
            delay = 3000;
            break;
          case '3g':
            delay = 2000;
            break;
          case '4g':
            delay = 1000;
            break;
        }
      }
    }

    // Adjust for device performance
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) {
      delay += 1000;
    }

    return delay;
  }

  private async loadGoogleAnalytics(): Promise<void> {
    if (!this.config.googleAnalytics.enabled || this.loadingState.analytics) {
      return;
    }

    this.loadingState.analytics = true;
    console.log('📊 [ThirdPartyLoader] Loading Google Analytics...');

    try {
      // Initialize dataLayer
      (window as any).dataLayer = (window as any).dataLayer || [];
      function gtag(...args: any[]) {
        (window as any).dataLayer.push(args);
      }
      (window as any).gtag = gtag;

      // Load gtag script
      await this.loadScript(
        `https://www.googletagmanager.com/gtag/js?id=${this.config.googleAnalytics.id}`,
        'google-analytics'
      );

      // Configure analytics with environment-specific settings
      gtag('js', new Date());
      gtag('config', this.config.googleAnalytics.id, {
        // Enhanced privacy settings
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        // ✅ FIX: Add debug mode for non-production
        debug_mode: process.env.NODE_ENV !== 'production',
        // ✅ FIX: Ensure page view is sent
        send_page_view: true,
        // ✅ FIX: Add custom parameters for better tracking
        custom_map: {
          'custom_dimension_1': 'page_type',
          'custom_dimension_2': 'user_type'
        }
      });

      // ✅ FIX: Send initial page view to ensure tracking starts
      gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        custom_dimension_1: 'main_site',
        custom_dimension_2: 'visitor'
      });

      console.log('✅ [ThirdPartyLoader] Google Analytics loaded successfully');
      console.log('🔍 [ThirdPartyLoader] Analytics Debug Mode:', process.env.NODE_ENV !== 'production');
    } catch (error) {
      console.warn('⚠️ [ThirdPartyLoader] Google Analytics failed to load:', error);
      this.loadingState.analytics = false;
    }
  }

  private async loadGoogleAdsense(): Promise<void> {
    if (!this.config.googleAdsense.enabled || this.loadingState.adsense) {
      return;
    }

    this.loadingState.adsense = true;
    console.log('💰 [ThirdPartyLoader] Loading Google AdSense...');

    try {
      await this.loadScript(
        `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${this.config.googleAdsense.client}`,
        'google-adsense',
        { crossOrigin: 'anonymous' }
      );

      console.log('✅ [ThirdPartyLoader] Google AdSense loaded successfully');
    } catch (error) {
      console.warn('⚠️ [ThirdPartyLoader] Google AdSense failed to load:', error);
      this.loadingState.adsense = false;
    }
  }

  private loadScript(
    src: string, 
    id: string, 
    options: { crossOrigin?: string } = {}
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script already exists
      if (document.getElementById(id)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = id;
      script.async = true;
      script.src = src;

      if (options.crossOrigin) {
        script.crossOrigin = options.crossOrigin;
      }

      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

      document.head.appendChild(script);
    });
  }

  private setupLoadingTriggers(): void {
    const delay = this.getOptimalDelay();

    // Trigger 1: Time-based delay
    const timeoutId = setTimeout(() => {
      this.loadAllScripts();
    }, delay);
    this.timeouts.push(timeoutId);

    // Trigger 2: User interaction
    this.setupUserInteractionTrigger();

    // Trigger 3: Content visibility
    this.setupVisibilityTrigger();

    // Trigger 4: React app ready
    this.setupReactReadyTrigger();
  }

  private setupUserInteractionTrigger(): void {
    const events = ['scroll', 'click', 'touchstart', 'keydown'] as const;
    let triggered = false;

    const handleInteraction = () => {
      if (triggered) return;
      triggered = true;

      console.log('👆 [ThirdPartyLoader] User interaction detected, loading scripts...');
      const timeoutId = setTimeout(() => this.loadAllScripts(), 500);
      this.timeouts.push(timeoutId);

      // Remove listeners
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };

    events.forEach(event => {
      document.addEventListener(event, handleInteraction, { passive: true });
    });
  }

  private setupVisibilityTrigger(): void {
    if (!window.IntersectionObserver) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            console.log('👀 [ThirdPartyLoader] Main content visible, loading scripts...');
            const timeoutId = setTimeout(() => this.loadAllScripts(), 300);
            this.timeouts.push(timeoutId);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    // Wait for React root to be available
    const checkRoot = () => {
      const root = document.getElementById('root');
      if (root && root.children.length > 0) {
        observer.observe(root);
        this.observers.push(observer);
      } else {
        const timeoutId = setTimeout(checkRoot, 500);
        this.timeouts.push(timeoutId);
      }
    };

    checkRoot();
  }

  private setupReactReadyTrigger(): void {
    // Listen for React app ready signal
    window.addEventListener('react-app-ready', () => {
      console.log('⚛️ [ThirdPartyLoader] React app ready, loading scripts...');
      const timeoutId = setTimeout(() => this.loadAllScripts(), 1000);
      this.timeouts.push(timeoutId);
    });
  }

  private async loadAllScripts(): Promise<void> {
    if (this.loadingState.analytics && this.loadingState.adsense) {
      return; // Already loaded
    }

    console.log('🚀 [ThirdPartyLoader] Loading all third-party scripts...');

    // Use requestIdleCallback for better performance
    const loadScripts = async () => {
      try {
        // Load with small delays to avoid overwhelming the browser
        await this.loadGoogleAnalytics();
        
        await new Promise(resolve => setTimeout(resolve, 200));
        await this.loadGoogleAdsense();

      } catch (error) {
        console.error('❌ [ThirdPartyLoader] Error loading scripts:', error);
      }
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(loadScripts, { timeout: 2000 });
    } else {
      setTimeout(loadScripts, 100);
    }
  }

  // Public methods
  public isAnalyticsLoaded(): boolean {
    return this.loadingState.analytics;
  }

  public isAdsenseLoaded(): boolean {
    return this.loadingState.adsense;
  }

  public forceLoad(): void {
    console.log('🔧 [ThirdPartyLoader] Force loading requested');
    this.loadAllScripts();
  }

  public trackEvent(eventName: string, parameters?: Record<string, any>): void {
    if (this.isAnalyticsLoaded() && (window as any).gtag) {
      (window as any).gtag('event', eventName, parameters);
    } else {
      console.warn('⚠️ [ThirdPartyLoader] Analytics not loaded, cannot track event:', eventName);
    }
  }

  public cleanup(): void {
    // Clear timeouts
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts = [];

    // Disconnect observers
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];

    console.log('🧹 [ThirdPartyLoader] Cleanup completed');
  }
}

// Singleton instance
let thirdPartyLoader: ThirdPartyLoader | null = null;

export const getThirdPartyLoader = (): ThirdPartyLoader => {
  if (!thirdPartyLoader) {
    thirdPartyLoader = new ThirdPartyLoader();
  }
  return thirdPartyLoader;
};

export default getThirdPartyLoader;

// Global type declarations
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
} 